<template>
  <button
    class="btn btn-ghost-primary"
    type="button"
    @click="showModal = true"
    title="Favorite Links"
  >
    <i class="fa fa-star"></i>
  </button>

  <!-- Modal -->
  <Teleport to="body">
    <div
      class="modal fade"
      id="modal-favorites"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modal-favorites"
      :class="{ show: showModal }"
      :style="{ display: showModal ? 'block' : 'none' }"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Favorite Links</h5>
            <button
              type="button"
              class="btn-close"
              @click="showModal = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="isLoading || isStoreLoading" class="loading-overlay">
              <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
              <p class="mt-2">Saving changes...</p>
            </div>

            <div
              v-if="!isReady && !isLoading && !isStoreLoading"
              class="text-center py-4"
            >
              <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
              <p class="mt-2">Loading...</p>
            </div>

            <div
              v-else-if="!hasSavedLinks && !isLoading && !isStoreLoading"
              class="text-center py-4"
            >
              <i class="fa fa-star fa-3x text-muted mb-3"></i>
              <p>You don't have any favorite links for this site yet.</p>
              <p class="text-muted">Add your first favorite link!</p>
            </div>

            <div v-else>
              <ul class="list-group mb-4">
                <li
                  v-for="(link, index) in currentSiteLinks"
                  :key="index"
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div
                    class="d-flex align-items-center flex-grow-1 text-truncate"
                  >
                    <a
                      :href="link.url"
                      :title="link.url"
                      target="_blank"
                      class="link-title text-decoration-none text-truncate"
                    >
                      {{ link.title }}
                    </a>
                  </div>
                  <div class="btn-group ms-2 flex-shrink-0">
                    <a
                      :href="link.url"
                      target="_blank"
                      class="btn btn-sm btn-alt-primary"
                      title="Open in new tab"
                    >
                      <i class="fa fa-external-link"></i>
                    </a>
                    <button
                      v-if="!link.isAutomatic"
                      type="button"
                      class="btn btn-sm btn-alt-secondary"
                      @click="editLinkIndex = index"
                      title="Edit"
                      :disabled="isLoading || isStoreLoading"
                    >
                      <i class="fa fa-edit"></i>
                    </button>
                    <button
                      v-if="!link.isAutomatic"
                      type="button"
                      class="btn btn-sm btn-alt-danger"
                      @click="removeLink(index)"
                      title="Delete"
                      :disabled="isLoading || isStoreLoading"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </li>
              </ul>
            </div>

            <div v-if="editLinkIndex !== null" class="card">
              <div class="card-header">
                <h5 class="mb-0">
                  {{ editLinkIndex === -1 ? 'Add New Link' : 'Edit Link' }}
                </h5>
              </div>
              <div class="card-body">
                <div class="modal-body py-3">
                  <form @submit.prevent="saveLink()">
                    <div class="mb-3">
                      <label for="title" class="form-label">Title</label>
                      <input
                        type="text"
                        id="title"
                        v-model="editingLink.title"
                        class="form-control"
                        required
                        :disabled="isLoading || isStoreLoading"
                      />
                    </div>
                    <div class="mb-3">
                      <label for="url" class="form-label">URL</label>
                      <input
                        type="url"
                        id="url"
                        v-model="editingLink.url"
                        class="form-control"
                        required
                        :disabled="isLoading || isStoreLoading"
                      />
                    </div>
                    <div class="mt-4 d-flex justify-content-end">
                      <button
                        type="button"
                        class="btn btn-secondary me-2"
                        @click="cancelEdit"
                        :disabled="isLoading || isStoreLoading"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="isLoading || isStoreLoading"
                      >
                        <i
                          v-if="isLoading"
                          class="fa fa-spinner fa-spin me-1"
                        ></i>
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <button
              v-if="editLinkIndex === null"
              type="button"
              class="btn btn-alt-primary w-100"
              @click="startAddingNewLink"
              :disabled="isLoading || isStoreLoading"
            >
              <i class="fa fa-plus me-1"></i> Add New Link
            </button>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showModal = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
  import { ref, computed, watch } from 'vue'
  import { useFavoriteLinksStore } from '@/stores/favoriteLinks'
  import { useTemplateStore } from '@/stores/template'
  import Swal from 'sweetalert2'

  const favoriteLinksStore = useFavoriteLinksStore()
  const templateStore = useTemplateStore()

  const showModal = ref(false)
  const editLinkIndex = ref(null)
  const editingLink = ref({ title: '', url: '' })
  const isLoading = ref(false)

  const currentSite = computed(() => templateStore.site)

  const currentSiteLinks = computed(() => {
    return favoriteLinksStore.getLinksForSite(currentSite.value)
  })

  const hasSavedLinks = computed(() => {
    return currentSiteLinks.value.length > 0
  })

  const isStoreLoading = computed(() => {
    return favoriteLinksStore.isLoading
  })

  const isReadyKeys = ['config']
  const isReady = computed(() => {
    return isReadyKeys
      .map(key => !templateStore.isLoading.includes(key))
      .every(Boolean)
  })

  watch(showModal, isOpen => {
    if (isOpen && !currentSiteLinks.value.length) {
      favoriteLinksStore.initFromConfig()
    }
  })

  watch(
    () => currentSite.value,
    () => {
      showModal.value = false
      cancelEdit()
    }
  )

  function startAddingNewLink() {
    editingLink.value = { title: '', url: '' }
    editLinkIndex.value = -1
  }

  function cancelEdit() {
    editLinkIndex.value = null
    editingLink.value = { title: '', url: '' }
  }

  watch(editLinkIndex, newIndex => {
    if (
      newIndex !== null &&
      newIndex >= 0 &&
      newIndex < currentSiteLinks.value.length
    ) {
      const link = currentSiteLinks.value[newIndex]
      if (link) {
        editingLink.value = { ...link }
      }
    }
  })

  function showToast(message, type = 'success') {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: toast => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    })

    Toast.fire({
      icon: type,
      title: message
    })
  }

  async function saveLink() {
    isLoading.value = true

    try {
      let result = false
      let message = ''

      if (editLinkIndex.value === -1) {
        result = await favoriteLinksStore.addLink(currentSite.value, {
          ...editingLink.value
        })
        message = 'Link added successfully!'
      } else {
        result = await favoriteLinksStore.editLink(
          currentSite.value,
          editLinkIndex.value,
          { ...editingLink.value }
        )
        message = 'Link updated successfully!'
      }

      if (result) {
        showToast(message, 'success')
        cancelEdit()
      } else {
        showToast('Error saving link', 'error')
      }
    } catch (error) {
      console.error('Error saving link:', error)
      showToast('Error saving link: ' + error.message, 'error')
    } finally {
      isLoading.value = false
    }
  }

  async function removeLink(index) {
    try {
      const result = await Swal.fire({
        title: 'Delete link',
        text: 'Are you sure you want to delete this link?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      })

      if (result.isConfirmed) {
        isLoading.value = true
        await favoriteLinksStore.removeLink(currentSite.value, index)
        showToast('Link removed successfully!', 'success')
      }
    } catch (error) {
      console.error('Error removing link:', error)
      showToast('Error removing link: ' + error.message, 'error')
    } finally {
      isLoading.value = false
    }
  }
</script>

<style scoped>
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    border-radius: 0.25rem;
  }

  .fa-star {
    color: #7d2ac2;
  }
</style>
